import React, { useState, useEffect } from 'react';
import { 
  MdWeb, MdSmartphone, MdBusinessCenter, MdCloud, 
  MdSecurity, MdSmartToy, MdBuild, MdDns, MdWifi, 
  MdArrowForward, MdEmail, MdPhone, MdClose, MdMenu,
  MdOutlineLightMode, MdOutlineDarkMode
} from "react-icons/md";

/**
 * MTech Solutions - Unified Professional IT Landing Page
 * Features: Framer-like animations (CSS), Interactive Hero, Dark Mode, Responsive Design.
 */

const App = () => {
  const [darkMode, setDarkMode] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [gradientPos, setGradientPos] = useState({ x: 50, y: 50 });

  // Handle Dark Mode Persistence
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // Interactive Background Logic
  const handleMouseMove = (e) => {
    const { clientX, clientY } = e;
    const { innerWidth, innerHeight } = window;
    setGradientPos({
      x: (clientX / innerWidth) * 100,
      y: (clientY / innerHeight) * 100,
    });
  };

const services = [
    { title: "Web Development", desc: "Modern, responsive websites and web applications built with latest technologies.", icon: <MdWeb /> },
    { title: "Mobile App Development", desc: "Native and cross-platform mobile apps for iOS and Android.", icon: <MdSmartphone /> },
    { title: "IT Consulting", desc: "Expert guidance to help your business leverage technology effectively.", icon: <MdBusinessCenter /> },
    { title: "Networking", desc: "Reliable network design, setup, and optimization for seamless connectivity.", icon: <MdWifi /> },
    { title: "Hardware Repair", desc: "Professional diagnostics and repair for your office hardware and equipment.", icon: <MdBuild /> },
    { title: "Infrastructure", desc: "Robust server management, virtualization, and infrastructure setup.", icon: <MdDns /> },
    { title: "AI & Automation", desc: "Intelligent automation and AI-powered business solutions.", icon: <MdSmartToy /> },
    { title: "Cloud Solutions", desc: "AWS, Azure & Google Cloud implementation and migration.", icon: <MdCloud /> },
    { title: "Cybersecurity", desc: "Protect your business with robust security solutions and audits.", icon: <MdSecurity /> },
  ];

  const projects = [
    { name: "Maths Gem Seychelles", client: "eLearning Portal", image: "/assets/mathsgemlogo.png", link: "https://mathsgemseychelles.com/" },
    { name: "National Sports Council", client: "CJSOI Web Application", image: "/assets/National Sports Council Logo.png", link: "https://cjsoi2025.web.app/" },
    { name: "Cat Cocos", client: "Video Management Portal", image: "/assets/CatCocos.png", },
  ];

  const navLinks = [
    { name: 'Services', href: '#services' },
    { name: 'Projects', href: '#projects' },
    { name: 'About', href: '#about' },
    { name: 'Contact', href: '#contact' },
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0a0a0b] text-slate-900 dark:text-slate-100 selection:bg-blue-100 selection:text-blue-900 transition-colors duration-300">
      
      {/* 1. NAVIGATION */}
      <nav className="fixed top-0 w-full z-50 bg-white/80 dark:bg-[#0a0a0b]/80 backdrop-blur-md border-b border-slate-200 dark:border-white/10">
        <div className="max-w-7xl mx-auto px-6 h-20 flex justify-between items-center">
          <div className="flex items-center group cursor-pointer">
            <span className="font-extrabold text-2xl tracking-tighter text-[#034992] dark:text-blue-500">MTech</span>
            <span className="text-slate-400 dark:text-slate-500 text-2xl font-light ml-1 group-hover:text-slate-600 transition-colors">Solutions</span>
          </div>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-10 text-md font-bold tracking-widest">
            {navLinks.map((link) => (
              <a key={link.name} href={link.href} className="hover:text-[#034992] dark:hover:text-blue-400 transition-colors">
                {link.name}
              </a>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <button 
              onClick={() => setDarkMode(!darkMode)} 
              className="p-2.5 rounded-xl border border-slate-200 dark:border-white/10 hover:bg-slate-100 dark:hover:bg-white/5 transition-all text-xl"
            >
              {darkMode ? <MdOutlineLightMode /> : <MdOutlineDarkMode />}
            </button>
            <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden p-2 text-3xl">
              {menuOpen ? <MdClose /> : <MdMenu />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        {menuOpen && (
          <div className="md:hidden bg-white dark:bg-[#0a0a0b] border-b border-slate-200 dark:border-white/10 px-6 py-8 flex flex-col gap-6 font-bold uppercase tracking-widest text-center animate-fade-in">
            {navLinks.map((link) => (
              <a key={link.name} href={link.href} onClick={() => setMenuOpen(false)}>{link.name}</a>
            ))}
          </div>
        )}
      </nav>

      {/* 2. HERO SECTION */}
      <section 
        className="relative pt-32 pb-24 lg:pt-24 lg:pb-24 overflow-hidden"
        onMouseMove={handleMouseMove}
      >
        {/* Dynamic Spotlight Background */}
        <div 
          className="absolute inset-0 z-0 pointer-events-none transition-opacity duration-700 opacity-40 dark:opacity-20"
          style={{
            background: `radial-gradient(circle at ${gradientPos.x}% ${gradientPos.y}%, rgba(3, 73, 146, 0.4) 0%, rgba(3, 73, 146, 0) 50%)`,
          }}
        />

        <div className="max-w-7xl mx-auto px-6 relative z-10 grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Content */}
          <div className="max-w-4xl order-2 lg:order-1">
            <h1 className="text-5xl md:text-6xl font-black leading-[1.05] tracking-tight mb-8 animate-slide-up">
              <span className="text-[#034992] dark:text-blue-500">Accelerating</span> your<br />
              Digital <span className="relative inline-block">Evolution.</span>
            </h1>

            <p className="text-lg md:text-2xl text-slate-600 dark:text-slate-400 mb-12 max-w-2xl leading-relaxed animate-scale-in">
              We build, secure, and scale technology environments that allow your business to operate without limits.
            </p>

            <div className="flex flex-col sm:flex-row gap-5 animate-fade-in-delayed">
              <a href="#contact" className="px-10 py-5 bg-[#034992] hover:bg-[#023a75] text-white rounded-2xl font-bold transition-all shadow-xl shadow-blue-900/20 flex items-center justify-center gap-3 group transform hover:-translate-y-1">
                Consult With Us <MdArrowForward className="group-hover:translate-x-2 transition-transform" />
              </a>
              <a href="#services" className="px-10 py-5 border border-slate-300 dark:border-white/10 hover:bg-slate-100 dark:hover:bg-white/5 rounded-2xl font-bold transition-all text-center transform hover:-translate-y-1">
                View Services
              </a>
            </div>
          </div>
          
          {/* Right Column - Illustration */}
          <div className="order-1 lg:order-2 flex justify-center lg:justify-end animate-float">
            <img 
              src="/assets/MTech Hero.png" // Point to your saved illustration image
              alt="MTech Enterprise Software Solutions Bento Grid Mockup" 
              className="max-w-[40%] lg:max-w-[80%] lg:min-w-[40%] h-auto z-10"
            />
          </div>
        </div>
      </section>

      {/* 3. SERVICES SECTION */}
      <section id="services" className="py-24 bg-white dark:bg-[#0e0e10]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-20">
            <div>
              <h2 className="text-blue-600 font-bold uppercase tracking-[0.2em] mb-4 text-sm">Capabilities</h2>
              <h3 className="text-4xl md:text-5xl font-bold tracking-tight">Enterprise Solutions</h3>
            </div>
            <p className="text-slate-500 max-w-sm mt-4 md:mt-0 text-lg border-l-4 border-blue-500 pl-6 py-2">
              Bespoke technology services designed for local and global impact.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="group p-10 rounded-3xl bg-slate-50 dark:bg-white/5 border border-transparent hover:border-blue-500/30 hover:shadow-2xl hover:shadow-blue-500/5 transition-all duration-500">
                <div className="w-16 h-16 bg-white dark:bg-white/10 rounded-2xl flex items-center justify-center text-4xl text-[#034992] dark:text-blue-400 mb-8 shadow-sm group-hover:bg-[#034992] group-hover:text-white transition-all duration-300">
                  {service.icon}
                </div>
                <h4 className="text-2xl font-bold mb-4">{service.title}</h4>
                <p className="text-slate-600 dark:text-slate-400 leading-relaxed">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. PROJECTS SECTION */}
      <section id="projects" className="py-24 bg-slate-50 dark:bg-[#0a0a0b]">
  <div className="max-w-7xl mx-auto px-6">
    <div className="text-center mb-16">
      <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">Case Studies</h2>
      <div className="h-1.5 w-24 bg-[#034992] mx-auto rounded-full"></div>
    </div>

    <div className="grid md:grid-cols-3 gap-10">
      {projects.map((project, index) => (
        /* Wrap everything in a link tag */
        <a 
          key={index} 
          href={project.link} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="group block cursor-pointer"
        >
          <div className="aspect-[16/10] bg-white dark:bg-white/5 rounded-[2rem] flex items-center justify-center p-12 mb-6 border border-slate-200 dark:border-white/10 group-hover:border-blue-500/50 group-hover:shadow-3xl transition-all duration-500 overflow-hidden">
             <img 
              src={project.image} 
              alt={project.name} 
              className="max-h-full object-contain grayscale group-hover:grayscale-0 group-hover:scale-110 transition-all duration-700" 
            />
          </div>
          <span className="text-xs font-black text-blue-600 uppercase tracking-widest">
            {project.client}
          </span>
          <h4 className="text-2xl font-bold mt-2 group-hover:text-[#034992] dark:group-hover:text-blue-400 transition-colors">
            {project.name}
          </h4>
        </a>
      ))}
    </div>
  </div>
</section>

      {/* 5. ABOUT SECTION */}
      <section id="about" className="py-24 bg-white dark:bg-[#0e0e10]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <div>
              <h2 className="text-blue-600 font-bold uppercase tracking-[0.2em] mb-4 text-sm">Our Story</h2>
              <h3 className="text-4xl md:text-6xl font-bold tracking-tight mb-8">Bridging the Gap Between Ambition and IT.</h3>
              <div className="space-y-6 text-slate-600 dark:text-slate-400 text-lg leading-relaxed">
                <p>MTech Solutions was founded with a clear vision: to provide enterprise-grade technology to organizations throughout the Seychelles. We understand that IT is the backbone of modern business.</p>
                <p>Our methodology combines rigorous engineering with a boutique approach to client service, ensuring that your unique challenges are met with precise, scalable solutions.</p>
              </div>
            </div>
            <div className="bg-slate-100 dark:bg-white/5 p-4 rounded-[3rem] border border-slate-200 dark:border-white/10 shadow-inner">
               <img src="/assets/MTech Solutions Logo.png" alt="MTech HQ" className="w-full h-auto rounded-[2.5rem] opacity-90 hover:opacity-100 transition-opacity" />
            </div>
          </div>
        </div>
      </section>

      {/* 6. CONTACT SECTION */}
      <section id="contact" className="py-32 bg-[#034992] text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-400/20 rounded-full blur-[100px] -mr-48 -mt-48 animate-pulse"></div>
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-4xl md:text-7xl font-bold mb-10 tracking-tight">Ready to modernize?</h2>
          
          <div className="grid md:grid-cols-2 gap-8 text-left">
            <a href="mailto:mtechsolutionsey@gmail.com" className="group p-10 bg-white/10 backdrop-blur-xl rounded-[2rem] border border-white/20 hover:bg-white/20 transition-all transform hover:-translate-y-2">
              <MdEmail className="text-5xl mb-6 text-blue-200" />
              <div className="text-xs uppercase tracking-[0.2em] opacity-60 mb-2 font-bold">Email Inquiry</div>
              <div className="text-xl md:text-xl font-bold break-all">mtechsolutionsey@gmail.com</div>
            </a>
            <a href="tel:+2482756254" className="group p-10 bg-white/10 backdrop-blur-xl rounded-[2rem] border border-white/20 hover:bg-white/20 transition-all transform hover:-translate-y-2">
              <MdPhone className="text-5xl mb-6 text-blue-200" />
              <div className="text-xs uppercase tracking-[0.2em] opacity-60 mb-2 font-bold">Direct Line</div>
              <div className="text-xl font-bold">+248 275 62 54</div>
            </a>
          </div>

          <p className="mt-24 text-white/40 text-xs font-bold uppercase tracking-[0.4em]">
            © {new Date().getFullYear()} MTech Solutions • Seychelles • Professional IT Services
          </p>
        </div>
      </section>

      {/* CUSTOM CSS ANIMATIONS */}
      <style jsx global>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(40px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.97); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-fade-in { animation: fadeIn 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .animate-fade-in-delayed { animation: fadeIn 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.4s forwards; opacity: 0; }
        .animate-slide-up { animation: slideUp 1s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .animate-scale-in { animation: scaleIn 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.2s forwards; opacity: 0; }
        html { scroll-behavior: smooth; }
      `}</style>
    </div>
  );
};

export default App;